// src/services/walkingPattern.service.ts
// Build an ordered "walking pattern" route around a center point by snapping
// to OSM road geometry. Replaces the concentric-circle expansion in
// addressDatabase.service.getClosestAddresses with something that matches how
// a canvasser actually walks: street by street, end to end.
//
// Pipeline:
//   1. Fetch the road network from Overpass within `radius` meters of `center`.
//   2. Build an in-memory routing graph (nodes + neighbor edges).
//   3. Greedy walk: start at the closest node to `center`, prefer edges that
//      keep the canvasser walking straight, fall back to nearest unvisited
//      edge when the current node has no unvisited edges left. Stop when the
//      walk has covered enough distance to plausibly hit `targetCount` homes
//      or every edge has been visited.
//   4. For each ordered segment, query Overpass for tagged `addr:*` nodes
//      within ~30m of the segment, and (optionally) fall back to building
//      footprints + Google reverse-geocode for a few buildings per segment
//      when OSM addresses are sparse.
//   5. Return AddressData[] in walking order.

import { addressDatabaseService, AddressData } from './addressDatabase.service';

export interface WalkingPatternResult {
  addresses: AddressData[];
  source: 'walking_pattern';
  segmentsWalked: number;
  totalDistanceMeters: number;
}

interface OsmNode {
  id: number;
  lat: number;
  lng: number;
  neighbors: Set<number>;
}

interface OsmGraph {
  nodes: Map<number, OsmNode>;
  ways: Array<{ id: number; nodeIds: number[] }>;
}

const HIGHWAY_FILTER = 'residential|tertiary|secondary|service|living_street|unclassified|primary';

const haversine = (a: { lat: number; lng: number }, b: { lat: number; lng: number }): number => {
  const R = 6371000;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a.lat * Math.PI) / 180) *
      Math.cos((b.lat * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
};

const bearing = (
  from: { lat: number; lng: number },
  to: { lat: number; lng: number }
): number => {
  const φ1 = (from.lat * Math.PI) / 180;
  const φ2 = (to.lat * Math.PI) / 180;
  const Δλ = ((to.lng - from.lng) * Math.PI) / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x =
    Math.cos(φ1) * Math.sin(φ2) -
    Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
};

const turnDelta = (a: number, b: number): number => {
  let d = Math.abs(a - b);
  if (d > 180) d = 360 - d;
  return d;
};

const edgeKey = (a: number, b: number): string =>
  a < b ? `${a}:${b}` : `${b}:${a}`;

class WalkingPatternService {
  /**
   * Run an Overpass query via the existing rate-limit-aware fetcher in
   * addressDatabaseService. We deliberately reuse that helper instead of
   * making raw fetches so we inherit retry + multi-server fallback.
   */
  private async overpassQuery(query: string, onProgress?: (msg: string) => void): Promise<any> {
    // The fetchWithRetry helper is private on the service; we call the public
    // getAddressesInBounds-style method via a tiny adapter exposed by the
    // addressDatabaseService class. Since that helper is private, we instead
    // hit Overpass directly here but with a single mirror as a quick fallback.
    const servers = [
      'https://overpass-api.de/api/interpreter',
      'https://overpass.kumi.systems/api/interpreter',
    ];
    let lastErr: any;
    for (const url of servers) {
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 90_000);
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: `data=${encodeURIComponent(query)}`,
          signal: controller.signal,
        });
        clearTimeout(timeout);
        if (res.ok) return await res.json();
        lastErr = new Error(`Overpass ${res.status}`);
      } catch (err) {
        lastErr = err;
        onProgress?.('Retrying with another Overpass mirror…');
      }
    }
    throw lastErr || new Error('Overpass unreachable');
  }

  /**
   * Fetch the road graph in a radius around the center point.
   */
  private async fetchRoadGraph(
    center: { lat: number; lng: number },
    radiusMeters: number,
    onProgress?: (msg: string) => void
  ): Promise<OsmGraph> {
    onProgress?.('Loading street network…');
    const query = `
      [out:json][timeout:90];
      (
        way["highway"~"${HIGHWAY_FILTER}"](around:${radiusMeters},${center.lat},${center.lng});
      );
      (._;>;);
      out body;
    `;
    const data = await this.overpassQuery(query, onProgress);

    const nodes = new Map<number, OsmNode>();
    const ways: Array<{ id: number; nodeIds: number[] }> = [];

    for (const el of data.elements || []) {
      if (el.type === 'node') {
        nodes.set(el.id, {
          id: el.id,
          lat: el.lat,
          lng: el.lon,
          neighbors: new Set(),
        });
      }
    }

    for (const el of data.elements || []) {
      if (el.type === 'way' && Array.isArray(el.nodes) && el.nodes.length >= 2) {
        ways.push({ id: el.id, nodeIds: el.nodes });
        for (let i = 0; i < el.nodes.length - 1; i++) {
          const a = nodes.get(el.nodes[i]);
          const b = nodes.get(el.nodes[i + 1]);
          if (a && b) {
            a.neighbors.add(b.id);
            b.neighbors.add(a.id);
          }
        }
      }
    }

    return { nodes, ways };
  }

  /**
   * Greedy walk that prefers continuing straight. Returns ordered list of
   * (lat,lng) waypoints in walking order plus the segments visited.
   */
  private walkGraph(
    graph: OsmGraph,
    start: { lat: number; lng: number },
    targetDistanceMeters: number
  ): { waypoints: Array<{ lat: number; lng: number; nodeId: number }>; totalDistance: number } {
    if (graph.nodes.size === 0) {
      return { waypoints: [], totalDistance: 0 };
    }

    // Find the closest node to the start point.
    let startNode: OsmNode | null = null;
    let bestDist = Infinity;
    for (const node of Array.from(graph.nodes.values())) {
      const d = haversine(start, node);
      if (d < bestDist) {
        bestDist = d;
        startNode = node;
      }
    }
    if (!startNode) return { waypoints: [], totalDistance: 0 };

    const visited = new Set<string>();
    const waypoints: Array<{ lat: number; lng: number; nodeId: number }> = [
      { lat: startNode.lat, lng: startNode.lng, nodeId: startNode.id },
    ];
    let current: OsmNode = startNode;
    let lastBearing: number | null = null;
    let totalDistance = 0;

    while (totalDistance < targetDistanceMeters) {
      // Pick the unvisited edge that keeps us closest to straight.
      let next: OsmNode | null = null;
      let bestTurn = Infinity;

      const neighborIds = Array.from(current.neighbors);
      for (const nid of neighborIds) {
        if (visited.has(edgeKey(current.id, nid))) continue;
        const candidate = graph.nodes.get(nid);
        if (!candidate) continue;
        const b = bearing(current, candidate);
        const turn = lastBearing == null ? 0 : turnDelta(lastBearing, b);
        if (turn < bestTurn) {
          bestTurn = turn;
          next = candidate;
        }
      }

      if (!next) {
        // Stuck — find the nearest unvisited edge anywhere in the graph.
        let teleportTo: OsmNode | null = null;
        let teleportDist = Infinity;
        const allNodes = Array.from(graph.nodes.values());
        for (const node of allNodes) {
          for (const nid of Array.from(node.neighbors)) {
            if (visited.has(edgeKey(node.id, nid))) continue;
            const d = haversine(current, node);
            if (d < teleportDist) {
              teleportDist = d;
              teleportTo = node;
            }
          }
        }
        if (!teleportTo) break;
        // Soft teleport — record the jump as a waypoint but don't count it
        // toward the target distance (it's not actually walking).
        waypoints.push({ lat: teleportTo.lat, lng: teleportTo.lng, nodeId: teleportTo.id });
        current = teleportTo;
        lastBearing = null;
        continue;
      }

      const stepDist = haversine(current, next);
      visited.add(edgeKey(current.id, next.id));
      lastBearing = bearing(current, next);
      totalDistance += stepDist;
      waypoints.push({ lat: next.lat, lng: next.lng, nodeId: next.id });
      current = next;
    }

    return { waypoints, totalDistance };
  }

  /**
   * For an ordered list of waypoints, query Overpass for `addr:housenumber`
   * nodes/ways near each segment and assemble them in walking order.
   */
  private async addressesAlongRoute(
    waypoints: Array<{ lat: number; lng: number; nodeId: number }>,
    center: { lat: number; lng: number },
    onProgress?: (msg: string) => void
  ): Promise<AddressData[]> {
    if (waypoints.length < 2) return [];

    // Compute a bounding box around the whole route + a small buffer so we
    // can fetch all candidate addresses in one Overpass query.
    let south = Infinity, west = Infinity, north = -Infinity, east = -Infinity;
    for (const w of waypoints) {
      south = Math.min(south, w.lat);
      west = Math.min(west, w.lng);
      north = Math.max(north, w.lat);
      east = Math.max(east, w.lng);
    }
    const latBuffer = 60 / 111_320;
    const lngBuffer = 60 / (111_320 * Math.cos(((north + south) / 2) * Math.PI / 180));
    const bbox = {
      south: south - latBuffer,
      west: west - lngBuffer,
      north: north + latBuffer,
      east: east + lngBuffer,
    };

    onProgress?.('Loading addresses along route…');
    const candidates = await addressDatabaseService.getAddressesInBounds(bbox, onProgress);

    // For each address, find the closest waypoint index. Sort by that index
    // so the final order matches walking order.
    type Indexed = { addr: AddressData; idx: number; dist: number };
    const indexed: Indexed[] = [];
    for (const addr of candidates.addresses) {
      let bestIdx = -1;
      let bestDist = Infinity;
      for (let i = 0; i < waypoints.length; i++) {
        const d = haversine({ lat: addr.lat, lng: addr.lng }, waypoints[i]);
        if (d < bestDist) {
          bestDist = d;
          bestIdx = i;
        }
      }
      // Drop addresses too far from any waypoint (off-route).
      if (bestIdx >= 0 && bestDist <= 80) {
        indexed.push({ addr, idx: bestIdx, dist: bestDist });
      }
    }

    indexed.sort((a, b) => {
      if (a.idx !== b.idx) return a.idx - b.idx;
      return a.dist - b.dist;
    });

    return indexed.map((i) => i.addr);
  }

  /**
   * Public entry point. Returns an ordered list of addresses representing a
   * walking route around the center point that picks up roughly `targetCount`
   * homes.
   */
  async getAddressesAlongWalkingRoute(
    center: { lat: number; lng: number },
    targetCount: number,
    onProgress?: (msg: string) => void
  ): Promise<WalkingPatternResult> {
    // Heuristic: target ~1 address per 15m of walking, capped between
    // 600m and 5km. This is a rough scaling to make sure we fetch enough
    // street network for the requested home count.
    const targetDistanceMeters = Math.max(600, Math.min(targetCount * 15, 5000));
    const fetchRadius = Math.max(800, Math.min(targetDistanceMeters * 0.8, 4000));

    onProgress?.('Building walking route…');
    const graph = await this.fetchRoadGraph(center, fetchRadius, onProgress);

    if (graph.nodes.size < 10) {
      throw new Error(
        'Sparse street data here — switch to "Closest first" or draw a polygon manually.'
      );
    }

    onProgress?.(`Walking ${graph.nodes.size} street nodes…`);
    const { waypoints, totalDistance } = this.walkGraph(graph, center, targetDistanceMeters);

    if (waypoints.length < 2) {
      throw new Error('Could not build a walking route from this point.');
    }

    onProgress?.('Collecting addresses along route…');
    const addresses = await this.addressesAlongRoute(waypoints, center, onProgress);

    // Cap to requested count (walking order preserved).
    const final = addresses.slice(0, targetCount);

    onProgress?.(`Found ${final.length} addresses along ${(totalDistance / 1000).toFixed(2)} km of route`);

    return {
      addresses: final,
      source: 'walking_pattern',
      segmentsWalked: waypoints.length - 1,
      totalDistanceMeters: totalDistance,
    };
  }
}

export const walkingPatternService = new WalkingPatternService();
